/**
 * DarkEarlyAccessForm Component
 * 
 * 3-Step wizard form for early access registration.
 * Broader audience: anyone who explains things to others.
 * Focuses on the "Spotlight Problem" - cognitive load of improvising explanations.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const DarkEarlyAccessForm = ({ onConfirmation, onBack }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: About You
    name: '',
    email: '',
    teaching_frequency: '',
    professional_intent: '',
    who_you_help: [],
    what_you_explain: '',
    
    // Step 2: Your Challenges
    challenges: [],
    challenges_other: '',
    
    // Step 3: How AANA Can Help + Goals
    aana_support: [],
    future_hosting: '',
    exciting_features: [],
    share_with_world: '',
    anything_else: '',
    
    // Optional (kept from old form)
    tools: [],
    tools_other: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hasSubmission, setHasSubmission] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [loadingSubmission, setLoadingSubmission] = useState(false);

  // Prefill if logged in and check for existing submission
  useEffect(() => {
    let isMounted = true;

    const checkExistingSubmission = async () => {
      if (!user?.email) return;
      
      setLoadingSubmission(true);
      try {
        const response = await fetch(`/api/early-access?email=${encodeURIComponent(user.email)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('accessToken') ? { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } : {})
          }
        });

        if (response.ok && isMounted) {
          const result = await response.json();
          if (result.success && result.data) {
            setHasSubmission(true);
            setFormData(prev => ({
              ...prev,
              name: result.data.name || user.name || user.username || '',
              email: result.data.email || user.email || '',
              teaching_frequency: result.data.teaching_frequency || '',
              professional_intent: result.data.professional_intent || '',
              who_you_help: result.data.who_you_help || [],
              what_you_explain: result.data.what_you_explain || '',
              challenges: result.data.challenges || [],
              challenges_other: result.data.challenges_other || '',
              aana_support: result.data.aana_support || [],
              future_hosting: result.data.future_hosting || '',
              exciting_features: result.data.exciting_features || [],
              share_with_world: result.data.share_with_world || '',
              anything_else: result.data.anything_else || ''
            }));
          } else {
            // no submission found, just prepopulate basic info
            setFormData(prev => ({
              ...prev,
              name: user.name || user.username || '',
              email: user.email || ''
            }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch existing submission", err);
      } finally {
        if (isMounted) setLoadingSubmission(false);
      }
    };

    checkExistingSubmission();

    return () => { isMounted = false; };
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleCheckboxChange = (e, field) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const current = prev[field];
      if (checked) return { ...prev, [field]: [...current, value] };
      return { ...prev, [field]: current.filter(item => item !== value) };
    });
  };

  // Step-specific validation
  const validateStep = (stepNum) => {
    const newErrors = {};
    
    if (stepNum === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
      if (!formData.teaching_frequency) newErrors.teaching_frequency = 'Please select how often you explain';
      if (!formData.professional_intent) newErrors.professional_intent = 'Please select an option';
      if (formData.who_you_help.length === 0) newErrors.who_you_help = 'Please select at least one option';
    }
    
    if (stepNum === 2) {
      if (formData.challenges.length === 0) newErrors.challenges = 'Please select at least one challenge';
    }
    
    if (stepNum === 3) {
      if (formData.aana_support.length === 0) newErrors.aana_support = 'Please select at least one option';
      if (!formData.future_hosting) newErrors.future_hosting = 'Please select an option';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;

    setSubmitting(true);
    try {
      const response = await fetch('/api/early-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken') ? { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } : {})
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        onConfirmation(data);
      } else {
        const errData = await response.json();
        console.error('Submission failed', errData);
        setErrors({ submit: 'Submission failed. Please try again.' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: 'An error occurred. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // ============ OPTIONS ============

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'occasionally', label: 'Occasionally' },
    { value: 'want_to_start', label: 'I want to start' }
  ];

  const professionalIntentOptions = [
    { value: 'already_do', label: 'Yes, I already do' },
    { value: 'planning_to', label: 'Yes, I\'m planning to' },
    { value: 'maybe_future', label: 'Maybe in the future' },
    { value: 'personal_only', label: 'No, just for personal/informal use' }
  ];

  const whoYouHelpOptions = [
    { value: 'friends_family', label: 'Yes, friends or family' },
    { value: 'siblings_children', label: 'Yes, younger siblings or children' },
    { value: 'work_university', label: 'Yes, at work or university' },
    { value: 'community_cultural', label: 'Yes, in my community or cultural group' },
    { value: 'online_content', label: 'Yes, online or through content' },
    { value: 'want_to', label: 'Not yet, but I want to' },
    { value: 'not_really', label: 'Not really' }
  ];

  const challengeOptions = [
    { value: 'structure_ideas', label: 'I know the idea but struggle to structure it' },
    { value: 'forget_next', label: 'I forget what to say next' },
    { value: 'cant_draw', label: "I can't draw diagrams well" },
    { value: 'lose_flow', label: 'I switch between ideas and lose flow' },
    { value: 'stuck_questions', label: 'I get stuck when people ask questions' },
    { value: 'lack_experience', label: 'I lack teaching experience' },
    { value: 'organise_lessons', label: "I don't know how to organise lessons" },
    { value: 'not_confident', label: "I'm not confident" }
  ];

  const aanaSupportOptions = [
    { value: 'auto_diagrams', label: 'Clear diagrams generated automatically' },
    { value: 'step_by_step', label: 'Step-by-step explanations' },
    { value: 'visual_scaffolds', label: 'Visual scaffolds I can talk through' },
    { value: 'analogies', label: 'Analogies and examples I can build on' },
    { value: 'outlines', label: 'Outlines that guide the session' },
    { value: 'unified_workspace', label: 'A workspace that brings everything together' },
    { value: 'bilingual', label: 'Help with bilingual explanations' },
    { value: 'recording', label: 'Recording and reviewing my sessions' },
    { value: 'exploring', label: "I'm not sure yet — I'm exploring" }
  ];

  const futureHostingOptions = [
    { value: 'yes_already', label: 'Yes, I already do or plan to' },
    { value: 'yes_try', label: 'Yes, I\'d try it' },
    { value: 'maybe_support', label: 'Maybe, with the right support' },
    { value: 'personal_use', label: 'I\'m more interested in personal use' }
  ];

  const excitingFeatureOptions = [
    { value: 'instant_visuals', label: 'Turning my ideas into clean visuals instantly' },
    { value: 'no_prep', label: 'Teaching without prep' },
    { value: 'more_confident', label: 'Feeling more confident in explanations' },
    { value: 'wider_audience', label: 'Sharing knowledge with a wider audience' },
    { value: 'informal_to_teachable', label: 'Turning informal experience into something teachable' },
    { value: 'help_family', label: 'Helping family or community members learn better' },
    { value: 'no_teaching_skills', label: 'Building lessons without needing teaching skills' }
  ];

  // Optional tools (kept from old form)
  const toolsOptions = [
    'Zoom', 'Google Meet', 'WhatsApp', 'Discord', 'PDF tools', 
    'Whiteboard apps', 'Google Docs / Notion'
  ];

  // ============ STEP LABELS ============
  const stepLabels = ['About You', 'Your Challenges', 'How AANA Helps'];

  if (loadingSubmission) {
    return (
      <div className="dark-ea-form" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{color: '#9ca3af', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'}}>
          <span className="dark-ea-form__spinner" style={{width: '24px', height: '24px', borderTopColor: '#ff2e93'}} />
          Checking your status...
        </div>
      </div>
    );
  }

  if (hasSubmission && !showUpdateForm) {
    return (
      <div className="dark-ea-form">
        {onBack && (
          <button className="dark-ea-form__back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to options
          </button>
        )}
        <div className="dark-ea-form__card" style={{ padding: '4rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(34, 197, 94, 0.1)', 
            color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          </div>
          <h2 className="dark-ea-form__title" style={{ marginBottom: '1rem' }}>You're already on the list!</h2>
          <p className="dark-ea-form__subtitle" style={{ marginBottom: '2.5rem', maxWidth: '400px' }}>
            We have your early access request reserved. Need to change your answers or add more details before we launch?
          </p>
          <button 
            type="button" 
            className="dark-ea-form__submit" 
            onClick={() => setShowUpdateForm(true)}
            style={{ maxWidth: '300px' }}
          >
            Review & Update My Response
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dark-ea-form">
      {/* Back button */}
      {onBack && step === 1 && (
        <button className="dark-ea-form__back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to options
        </button>
      )}

      <div className="dark-ea-form__card">
          {hasSubmission && step === 1 && (
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              color: '#3b82f6',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              Updating your existing submission. All fields have been pre-filled with your previous answers!
            </div>
          )}
        {/* Step Indicator */}
        <div className="dark-ea-form__stepper">
          {stepLabels.map((label, idx) => (
            <div 
              key={idx} 
              className={`dark-ea-form__step ${step === idx + 1 ? 'dark-ea-form__step--active' : ''} ${step > idx + 1 ? 'dark-ea-form__step--done' : ''}`}
            >
              <div className="dark-ea-form__step-number">
                {step > idx + 1 ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : idx + 1}
              </div>
              <span className="dark-ea-form__step-label">{label}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="dark-ea-form__form">
          
          {/* ============ STEP 1: ABOUT YOU ============ */}
          {step === 1 && (
            <>
              <h2 className="dark-ea-form__title">About You</h2>
              <p className="dark-ea-form__subtitle">
                Tell us a bit about yourself and how you share knowledge.
              </p>

              {/* Name & Email Row */}
              <div className="dark-ea-form__row">
                <div className="dark-ea-form__group">
                  <label className="dark-ea-form__label">Name *</label>
                  <input
                    type="text"
                    name="name"
                    className={`dark-ea-form__input ${errors.name ? 'dark-ea-form__input--error' : ''}`}
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!!user}
                    placeholder="Your name"
                  />
                  {errors.name && <span className="dark-ea-form__error">{errors.name}</span>}
                </div>

                <div className="dark-ea-form__group">
                  <label className="dark-ea-form__label">Email *</label>
                  <input
                    type="email"
                    name="email"
                    className={`dark-ea-form__input ${errors.email ? 'dark-ea-form__input--error' : ''}`}
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!!user}
                    placeholder="you@example.com"
                  />
                  {errors.email && <span className="dark-ea-form__error">{errors.email}</span>}
                </div>
              </div>

              {user && (
                <p className="dark-ea-form__hint">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  Signed in as {user.email}
                </p>
              )}

              {/* Who You Help - First question after name/email */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">In your everyday life, do you ever help people understand things? *</label>
                <div className="dark-ea-form__checkbox-grid">
                  {whoYouHelpOptions.map(opt => (
                    <label key={opt.value} className="dark-ea-form__checkbox">
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={formData.who_you_help.includes(opt.value)}
                        onChange={(e) => handleCheckboxChange(e, 'who_you_help')}
                      />
                      <span className="dark-ea-form__checkbox-box">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="dark-ea-form__checkbox-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {errors.who_you_help && <span className="dark-ea-form__error">{errors.who_you_help}</span>}
              </div>

              {/* Teaching Frequency */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">How often do you find yourself explaining or teaching something to others? *</label>
                <select
                  name="teaching_frequency"
                  className={`dark-ea-form__select ${errors.teaching_frequency ? 'dark-ea-form__input--error' : ''}`}
                  value={formData.teaching_frequency}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {frequencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.teaching_frequency && <span className="dark-ea-form__error">{errors.teaching_frequency}</span>}
              </div>

              {/* Professional Intent */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">Are you interested in teaching or sharing knowledge professionally (even part-time)? *</label>
                <select
                  name="professional_intent"
                  className={`dark-ea-form__select ${errors.professional_intent ? 'dark-ea-form__input--error' : ''}`}
                  value={formData.professional_intent}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {professionalIntentOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.professional_intent && <span className="dark-ea-form__error">{errors.professional_intent}</span>}
              </div>

              {/* What You Explain */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">What kinds of things do you usually explain or help with?</label>
                <textarea
                  name="what_you_explain"
                  className="dark-ea-form__textarea"
                  value={formData.what_you_explain}
                  onChange={handleChange}
                  placeholder="Examples: homework, cultural topics, language explanations, life skills, hobbies, technical topics..."
                  rows={3}
                />
              </div>
            </>
          )}

          {/* ============ STEP 2: YOUR CHALLENGES ============ */}
          {step === 2 && (
            <>
              <h2 className="dark-ea-form__title">Your Challenges</h2>
              <p className="dark-ea-form__subtitle">
                What makes explaining things difficult for you? This helps us understand the "Spotlight Problem."
              </p>

              {/* Challenges */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">What makes explaining things difficult for you? *</label>
                <div className="dark-ea-form__checkbox-grid dark-ea-form__checkbox-grid--wide">
                  {challengeOptions.map(opt => (
                    <label key={opt.value} className="dark-ea-form__checkbox">
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={formData.challenges.includes(opt.value)}
                        onChange={(e) => handleCheckboxChange(e, 'challenges')}
                      />
                      <span className="dark-ea-form__checkbox-box">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="dark-ea-form__checkbox-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {errors.challenges && <span className="dark-ea-form__error">{errors.challenges}</span>}
              </div>

              {/* Other challenges */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">Other challenges?</label>
                <input
                  type="text"
                  name="challenges_other"
                  className="dark-ea-form__input"
                  value={formData.challenges_other}
                  onChange={handleChange}
                  placeholder="Anything else that makes explaining difficult..."
                />
              </div>

              {/* Tools (Optional) */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">How do you currently share or teach? (Optional)</label>
                <div className="dark-ea-form__checkbox-grid">
                  {toolsOptions.map(tool => (
                    <label key={tool} className="dark-ea-form__checkbox">
                      <input
                        type="checkbox"
                        value={tool}
                        checked={formData.tools.includes(tool)}
                        onChange={(e) => handleCheckboxChange(e, 'tools')}
                      />
                      <span className="dark-ea-form__checkbox-box">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="dark-ea-form__checkbox-label">{tool}</span>
                    </label>
                  ))}
                </div>
                <input
                  type="text"
                  name="tools_other"
                  className="dark-ea-form__input dark-ea-form__input--small"
                  placeholder="Other tools..."
                  value={formData.tools_other}
                  onChange={handleChange}
                />
              </div>
            </>
          )}

          {/* ============ STEP 3: HOW AANA HELPS + GOALS ============ */}
          {step === 3 && (
            <>
              <h2 className="dark-ea-form__title">How AANA Could Support You</h2>
              <p className="dark-ea-form__subtitle">
                What would help you teach or share knowledge more confidently?
              </p>

              {/* AANA Support */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">What would help you teach or share knowledge more confidently? *</label>
                <div className="dark-ea-form__checkbox-grid dark-ea-form__checkbox-grid--wide">
                  {aanaSupportOptions.map(opt => (
                    <label key={opt.value} className="dark-ea-form__checkbox">
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={formData.aana_support.includes(opt.value)}
                        onChange={(e) => handleCheckboxChange(e, 'aana_support')}
                      />
                      <span className="dark-ea-form__checkbox-box">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="dark-ea-form__checkbox-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
                {errors.aana_support && <span className="dark-ea-form__error">{errors.aana_support}</span>}
              </div>

              {/* Share with world */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">If AANA could help you share something with the world, what would it be?</label>
                <textarea
                  name="share_with_world"
                  className="dark-ea-form__textarea"
                  value={formData.share_with_world}
                  onChange={handleChange}
                  placeholder="What knowledge, skill, or experience would you share?"
                  rows={3}
                />
              </div>

              {/* Future hosting */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">If AANA made explaining effortless, would you consider teaching or mentoring? *</label>
                <select
                  name="future_hosting"
                  className={`dark-ea-form__select ${errors.future_hosting ? 'dark-ea-form__input--error' : ''}`}
                  value={formData.future_hosting}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  {futureHostingOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {errors.future_hosting && <span className="dark-ea-form__error">{errors.future_hosting}</span>}
              </div>

              {/* Exciting Features (Optional) */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">Which of these are exciting to you? (Optional)</label>
                <div className="dark-ea-form__checkbox-grid dark-ea-form__checkbox-grid--wide">
                  {excitingFeatureOptions.map(opt => (
                    <label key={opt.value} className="dark-ea-form__checkbox">
                      <input
                        type="checkbox"
                        value={opt.value}
                        checked={formData.exciting_features.includes(opt.value)}
                        onChange={(e) => handleCheckboxChange(e, 'exciting_features')}
                      />
                      <span className="dark-ea-form__checkbox-box">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      <span className="dark-ea-form__checkbox-label">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Anything Else */}
              <div className="dark-ea-form__group">
                <label className="dark-ea-form__label">Anything else you'd like us to know?</label>
                <textarea
                  name="anything_else"
                  className="dark-ea-form__textarea"
                  value={formData.anything_else}
                  onChange={handleChange}
                  placeholder="Thoughts, suggestions, or questions..."
                  rows={3}
                />
              </div>
            </>
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="dark-ea-form__submit-error">{errors.submit}</div>
          )}

          {/* Navigation Buttons */}
          <div className="dark-ea-form__nav">
            {step > 1 && (
              <button
                type="button"
                className="dark-ea-form__nav-btn dark-ea-form__nav-btn--back"
                onClick={prevStep}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>
            )}
            
            {step < 3 ? (
              <button
                type="button"
                className="dark-ea-form__nav-btn dark-ea-form__nav-btn--next"
                onClick={nextStep}
              >
                Continue
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                className="dark-ea-form__submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="dark-ea-form__spinner" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Response
                    <span className="dark-ea-form__arrow">→</span>
                  </>
                )}
              </button>
            )}
          </div>

          <p className="dark-ea-form__footnote">
            {user 
              ? "Your submission is linked to your account for priority access."
              : "Creating an account increases your chance of early access — but your submission is counted either way."
            }
          </p>
        </form>
      </div>
    </div>
  );
};

export default DarkEarlyAccessForm;
